<template>
  <div class="chart-card">
    <div class="chart-header">
      <div class="chart-title">Monthly Forecast Breakdown</div>
    </div>
    <apex-chart
      type="bar"
      height="320"
      :series="series"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import ApexChart from 'vue-apexcharts'

// Stacked bottom (Paid) to top (Tentative). Order drives the stack, legend and colours.
const CATEGORIES = [
  { key: 'paid', name: 'Paid', color: '#2ba97a' },
  { key: 'raised', name: 'Raised', color: '#34c38f' },
  { key: 'signed', name: 'Signed', color: '#3b82f6' },
  { key: 'quoted', name: 'Quoted', color: '#e0973a' },
  { key: 'recurring', name: 'Recurring', color: '#8b5cf6' },
  { key: 'tentative', name: 'Tentative', color: '#c5cae0' }
]

export default {
  components: { ApexChart },

  props: {
    months: { type: Array, required: true },
    // Monthly revenue breakdown computed on the backend (ForecastSummaryService::breakdown)
    breakdown: { type: Object, required: true },
    targets: { type: Object, required: true },
    totals: { type: Object, default: () => ({}) }
  },

  computed: {
    // Target line value in dollars (the monthly target is the same for every month).
    monthlyTarget() {
      const vals = Object.values(this.targets)
      return vals.length ? vals[0] / 100 : 0
    },

    // ApexCharts only auto-scales to the series, so the target annotation can
    // land above the plot area — include it in the axis max with headroom.
    yAxisMax() {
      const monthMax = Math.max(0, ...this.months.map(month => {
        const b = this.breakdown[month] || {}
        return CATEGORIES.reduce((sum, cat) => sum + (b[cat.key] || 0), 0)
      })) / 100
      const max = Math.max(monthMax, this.monthlyTarget)
      return max > 0 ? max + 10000 : undefined
    },

    series() {
      return CATEGORIES.map(cat => ({
        name: cat.name,
        data: this.months.map(month => {
          const b = this.breakdown[month] || {}
          return (b[cat.key] || 0) / 100 // cents -> dollars
        })
      }))
    },

    chartOptions() {
      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit'
        },
        colors: CATEGORIES.map(c => c.color),
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 0,
            dataLabels: {
              total: {
                enabled: true,
                formatter: val => this.formatCompact(val),
                offsetY: -4,
                style: { fontSize: '11px', fontWeight: 600, color: '#8b8fa7' }
              }
            }
          }
        },
        dataLabels: { enabled: false },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '12px',
          markers: { radius: 3 },
          itemMargin: { horizontal: 8 }
        },
        xaxis: {
          categories: this.months.map(m => this.formatMonth(m)),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: '#8b8fa7', fontSize: '12px' } }
        },
        yaxis: {
          max: this.yAxisMax,
          labels: {
            formatter: val => this.formatCompact(val),
            style: { colors: '#a0a4b8', fontSize: '11px' }
          }
        },
        grid: {
          borderColor: '#f0f1f7',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } }
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: 'dark',
          y: { formatter: val => this.formatCompact(val) }
        },
        annotations: this.monthlyTarget > 0 ? {
          yaxis: [{
            y: this.monthlyTarget,
            borderColor: '#ff6b6b',
            strokeDashArray: 4,
            label: {
              text: this.formatCompact(this.monthlyTarget) + ' Target',
              position: 'left',
              offsetX: 60,
              borderColor: '#ff6b6b',
              style: { color: '#fff', background: '#ff6b6b', fontSize: '10px', fontWeight: 600 }
            }
          }]
        } : {}
      }
    }
  },

  methods: {
    formatMonth(month) {
      const m = month.split('-')[1]
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return names[parseInt(m) - 1]
    },

    // Compact currency formatting. Receives a value already in dollars.
    formatCompact(dollars) {
      const d = Math.abs(dollars)
      if (d >= 1000000) return '$' + (d / 1000000).toFixed(1) + 'M'
      if (d >= 1000) return '$' + Math.round(d / 1000) + 'K'
      if (d === 0) return '$0'
      return '$' + Math.round(d)
    }
  }
}
</script>

<style scoped>
.chart-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8eaf2;
  padding: 24px;
  margin-bottom: 24px;
}
.chart-header {
  margin-bottom: 8px;
}
.chart-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e1e2d;
}
</style>
